import { useEffect, useMemo, useState } from 'react';
import { PANELS } from '@panels/constants';
import classnames from 'classnames';
import { getHubLaunchUrl } from '../../utils/util';
import useSidePanel from './hooks/useSidePanel';
import { useSearchParams } from '../modals/IndividualStudentGradebook/hooks/useSearchParams';
import { CLOSE_SIDE_PANEL } from './store/actions';
import {
  studentBlocks,
  assignmentBlocks,
  refreshGradebookEvents,
} from './constants';

import s from './SidePanel.module.css';

// TODO: Remove this once we have a proper solution for RNI-6064
// WORKAROUND: Prevent Rollup tree-shaking of PANELS import (RNI-6064)
console.log(PANELS);

function StudentFormInputs() {
  return (
    <>
      <input
        type="text"
        name="studentId"
        id="studentId"
        data-test-id="studentId"
      />
      {studentBlocks.map((block, index) => {
        return (
          <input
            type="text"
            key={block}
            id={`widget${index + 1}`}
            name="widgets[]"
            defaultValue={block}
            data-test-id={`input_${block}`}
          />
        );
      })}
    </>
  );
}

function AssignmentFormInputs() {
  return (
    <>
      {assignmentBlocks.map((block, index) => {
        return (
          <input
            type="text"
            key={block}
            id={`widget${index + 1}`}
            name="widgets[]"
            defaultValue={block}
            data-test-id={`input_${block}`}
          />
        );
      })}
    </>
  );
}

function SidePanel({
  courseId,
  lmsId,
  iamId,
  refreshGradebook,
  hasStudentReport,
  getStudentReportLink,
}) {
  const [params] = useSearchParams();
  const { state, dispatch, sidePanelRef, url } = useSidePanel();
  const {
    open,
    resourceId,
    studentId,
    isgUrl,
    courseId: currentCourseId,
    hasResponses,
    previousFocusSelector,
  } = state;
  const singleAssignUrl = `/courses/${courseId}/performance/assign/${resourceId}`;

  const hubLaunchUrl = useMemo(
    () => getHubLaunchUrl({ lmsId, courseId, resourceId }),
    [lmsId, courseId, resourceId],
  );

  useEffect(() => {
    const form = sidePanelRef.current;
    if (form.courseId && form.resourceId) {
      form.courseId.value = courseId;
      form.resourceId.value = resourceId;
      form.iamId.value = iamId;
      form.launchAssignmentUrl.value = hubLaunchUrl;
      form.studentGradeUrl.value = isgUrl;
      form.singleAssignUrl.value = singleAssignUrl;
      form.hasStudentReport.value = hasStudentReport;
      form.hasResponses.value = hasResponses;
      if (studentId) {
        form.studentId.value = studentId;
      }
      if (resourceId !== '' || studentId !== '') {
        form.submit();
      }
    }
  }, [
    courseId,
    resourceId,
    iamId,
    sidePanelRef,
    studentId,
    isgUrl,
    lmsId,
    singleAssignUrl,
    hasStudentReport,
    hasResponses,
    hubLaunchUrl,
  ]);

  useEffect(() => {
    const handleMessage = (msg) => {
      const { name } = msg.data;
      if (name === PANELS.CLOSE_SIDE_PANEL) {
        dispatch({ type: CLOSE_SIDE_PANEL });
      }
      if (name === PANELS.OPEN_SINGLE_ASSIGN_MODAL) {
        window.location.replace(singleAssignUrl);
      }
      if (refreshGradebookEvents.includes(name)) {
        refreshGradebook({ assignmentId: resourceId, studentId });
      }
      if (name === PANELS.STUDENT_PERFORMANCE_VIEW_RESPONSES) {
        const viewResponseLInk = `${hubLaunchUrl}&studentUserId=${studentId}`;
        window.location.replace(viewResponseLInk);
      }
      if (name === PANELS.STUDENT_PERFORMANCE_VIEW_ASSIGNMENT_REPORT) {
        const reportLink = getStudentReportLink(studentId, resourceId);
        window.location.replace(reportLink);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceId, studentId, hubLaunchUrl]);

  // Focus management: focus iframe when panel opens, restore focus when it closes
  useEffect(() => {
    if (open) {
      // Small delay to let the iframe render
      const timer = setTimeout(() => {
        const iframe = document.getElementById('side-panel-frame');
        if (iframe) {
          iframe.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    } else if (previousFocusSelector) {
      // Restore focus to the element that triggered the panel
      const element = document.querySelector(previousFocusSelector);
      if (element instanceof HTMLElement) {
        element.focus();
      }
    }
  }, [open, previousFocusSelector]);

  const currentStudentId = params.get('studentId');
  const currentPath = window.location.pathname;

  const [student, setStudent] = useState(currentStudentId);
  const [path, setPath] = useState(currentPath);

  useEffect(() => {
    if (
      open &&
      (student !== currentStudentId ||
        path !== currentPath ||
        courseId !== currentCourseId)
    ) {
      dispatch({ type: CLOSE_SIDE_PANEL });
    }
    setStudent(currentStudentId);
    setPath(currentPath);
    return () => {
      if (open) {
        dispatch({ type: CLOSE_SIDE_PANEL });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStudentId, currentPath]);

  return (
    <div
      className={classnames(s.sidePanelContainer, { [s.sidePanelOpen]: open })}
    >
      <form
        name="panel"
        method="post"
        action={`${url}/${courseId}/${resourceId}`}
        ref={sidePanelRef}
        target="side-panel-frame"
        hidden={true}
      >
        <input
          type="text"
          name="resourceId"
          id="resourceId"
          data-test-id="resourceId"
        />
        <input
          type="text"
          name="courseId"
          id="courseId"
          data-test-id="courseId"
          defaultValue={courseId}
        />
        <input type="text" name="iamId" id="iamId" data-test-id="iamId" />
        <input
          type="text"
          name="launchAssignmentUrl"
          id="launchAssignmentUrl"
          data-test-id="launchAssignmentUrl"
        />
        <input
          type="text"
          name="studentGradeUrl"
          id="studentGradeUrl"
          data-test-id="studentGradeUrl"
        />
        <input
          type="text"
          name="singleAssignUrl"
          id="singleAssignUrl"
          data-test-id="singleAssignUrl"
        />
        <input
          type="text"
          name="hasStudentReport"
          id="hasStudentReport"
          data-test-id="hasStudentReport"
        />
        <input
          type="text"
          name="hasResponses"
          id="hasResponses"
          data-test-id="hasResponses"
        />
        {studentId === '' ? <AssignmentFormInputs /> : <StudentFormInputs />}
        <button type="submit">submit</button>
      </form>
      <div
        data-test-id="side-panel-iframe-container"
        className={classnames(s.sidePanelIframeContainer, {
          [s.sidePanelIframeOpen]: open,
        })}
      >
        <iframe
          title="side panel"
          name="side-panel-frame"
          id="side-panel-frame"
          data-test-id="side-panel-frame"
          className={s.iframe}
          tabIndex={open ? undefined : -1}
        />
      </div>
    </div>
  );
}

export default SidePanel;
